/* Kop van de offertepagina: nummer, titel, status met signaal, en de feiten die ertoe
 * doen (bedrag, geldigheid, project, revisie, verstuurd of besluit). De status verander
 * je hier met een handeling, niet met een keuzelijst: verstuurd, geaccepteerd, afgewezen. */

import { useState, type Ref } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, CalendarCheck, Check, CheckCircle2, Copy, Download, ExternalLink, FileText, FolderUp, Loader2, Maximize2,
  MoreHorizontal, PanelRight, PanelRightClose, Pencil, RotateCcw, Trash2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Quote } from '@/types/quote';
import { Bewerkbaar, Feit } from '../../projectpagina/ui/ProjectKop';
import { datum, euro } from '../../projectpagina/ui/opmaak';
import { traject } from '../data/lijst';
import { signaal, weergave } from '../data/status';
import type { Revisie } from '../data/revisies';
import { SignaalLabel, StatusLabel } from './StatusLabel';

export type OfferteMetStatus = Quote & {
  sent_on?: string | null;
  sent_to?: string | null;
  sent_revision?: number | null;
  decided_on?: string | null;
  decision_note?: string | null;
  follow_up_todo_id?: string | null;
};

function Titel({ titel, onBewaar }: { titel: string; onBewaar: (t: string) => void }) {
  const [bezig, setBezig] = useState(false);
  const [concept, setConcept] = useState(titel);
  if (!bezig) {
    return (
      <button type="button" onClick={() => { setConcept(titel); setBezig(true); }} className="group -mx-1 flex min-w-0 items-baseline gap-2 rounded px-1 text-left hover:bg-muted" aria-label="Titel wijzigen">
        <h1 className="min-w-0 text-2xl font-semibold leading-tight tracking-tight">{titel}</h1>
        <Pencil className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
      </button>
    );
  }
  const klaar = (bewaar: boolean) => {
    setBezig(false);
    const t = concept.trim();
    if (bewaar && t && t !== titel) onBewaar(t);
  };
  return (
    <Input
      autoFocus
      aria-label="Titel"
      value={concept}
      onChange={(e) => setConcept(e.target.value)}
      onBlur={() => klaar(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') klaar(true);
        if (e.key === 'Escape') klaar(false);
      }}
      className="h-9 min-w-[16rem] flex-1 text-xl font-semibold"
    />
  );
}

function Bewaarstand({ stand }: { stand: 'saved' | 'saving' | 'unsaved' }) {
  const s = {
    saved: { Icoon: Check, tekst: 'Bewaard', klasse: 'text-muted-foreground' },
    saving: { Icoon: Loader2, tekst: 'Opslaan…', klasse: 'text-muted-foreground' },
    unsaved: { Icoon: AlertCircle, tekst: 'Niet bewaard', klasse: 'text-amber-600' },
  }[stand];
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs', s.klasse)} aria-live="polite">
      <s.Icoon className={cn('h-3 w-3', stand === 'saving' && 'animate-spin')} aria-hidden />
      {s.tekst}
    </span>
  );
}

export interface KopActies {
  onWisselPdf: () => void;
  onVerstuurd: () => void;
  onBesluit: (u: 'accepted' | 'declined') => void;
  onTerug: () => void;
  onProjectmap: () => void;
  onDownload: () => void;
  onPdfTab: () => void;
  onRevisie: () => void;
  onVolledigScherm: () => void;
  onDupliceer: () => void;
  onVerwijder: () => void;
  onTitel: (t: string) => void;
  onDatum: (veld: 'valid_until' | 'quote_date', dag: string | null) => void;
}

export function OfferteKop({
  quote, revisies, werkBedrag, bewaarstand, vandaag, terugNaar, bezigPdf, bezigStatus, pdfErnaast, kopRef, acties,
}: {
  quote: OfferteMetStatus;
  revisies: Revisie[];
  werkBedrag: number;
  bewaarstand: 'saved' | 'saving' | 'unsaved';
  vandaag: string;
  /** De offertelijst met het filter waarmee je hem verliet. */
  terugNaar: string;
  bezigPdf: boolean;
  bezigStatus: boolean;
  /** Staat de PDF rechts naast de offerte (brede schermen)? */
  pdfErnaast: boolean;
  kopRef?: Ref<HTMLElement>;
  acties: KopActies;
}) {
  const w = weergave(quote, vandaag);
  const sig = signaal(quote, vandaag);
  const t = traject(quote as any);
  const metProject = Boolean(quote.project_id);
  const huidig = quote.current_version ?? 1;
  const laatsteBestand = revisies.find((r) => r.file_name);
  const contact = quote.contact ? `${quote.contact.first_name} ${quote.contact.last_name}`.trim() : null;
  const onderregel = [
    quote.organization?.name,
    contact,
  ].filter(Boolean);

  return (
    <section ref={kopRef} aria-label="Offerte" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={terugNaar} className="-ml-1 inline-flex items-center gap-1.5 rounded px-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden />Offertes
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          {quote.status === 'draft' && (
            <Button variant="outline" size="sm" className="h-8" onClick={acties.onVerstuurd} disabled={bezigStatus}>
              <CalendarCheck className="mr-1.5 h-4 w-4" />Markeer als verstuurd
            </Button>
          )}
          {quote.status === 'sent' && (
            <>
              <Button variant="outline" size="sm" className="h-8 border-green-300 text-green-800 hover:bg-green-50" onClick={() => acties.onBesluit('accepted')} disabled={bezigStatus}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />Geaccepteerd
              </Button>
              <Button variant="outline" size="sm" className="h-8" onClick={() => acties.onBesluit('declined')} disabled={bezigStatus}>
                <XCircle className="mr-1.5 h-4 w-4" />Afgewezen
              </Button>
            </>
          )}
          {metProject ? (
            <Button size="sm" className="h-8" onClick={acties.onProjectmap} disabled={bezigPdf}>
              {bezigPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FolderUp className="mr-1.5 h-4 w-4" />}
              Opslaan in projectmap
            </Button>
          ) : (
            <Button size="sm" className="h-8" onClick={acties.onDownload} disabled={bezigPdf}>
              {bezigPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
              Download PDF
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="hidden h-8 w-8 xl:inline-flex"
            onClick={acties.onWisselPdf}
            aria-pressed={pdfErnaast}
            aria-label={pdfErnaast ? 'PDF verbergen' : 'PDF ernaast tonen'}
            title={pdfErnaast ? 'PDF verbergen' : 'PDF ernaast tonen'}
          >
            {pdfErnaast ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Meer acties">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {metProject && <DropdownMenuItem onSelect={acties.onDownload}><Download className="mr-2 h-4 w-4" />Download PDF</DropdownMenuItem>}
              <DropdownMenuItem onSelect={acties.onPdfTab}><ExternalLink className="mr-2 h-4 w-4" />PDF in nieuw tabblad</DropdownMenuItem>
              <DropdownMenuItem onSelect={acties.onVolledigScherm}><Maximize2 className="mr-2 h-4 w-4" />Volledig scherm</DropdownMenuItem>
              <DropdownMenuItem onSelect={acties.onRevisie}><FileText className="mr-2 h-4 w-4" />Revisie vastleggen zonder PDF</DropdownMenuItem>
              <DropdownMenuSeparator />
              {quote.status === 'sent' && <DropdownMenuItem onSelect={acties.onVerstuurd}><CalendarCheck className="mr-2 h-4 w-4" />Opnieuw als verstuurd markeren</DropdownMenuItem>}
              {quote.status === 'sent' && <DropdownMenuItem onSelect={acties.onTerug}><RotateCcw className="mr-2 h-4 w-4" />Terug naar concept</DropdownMenuItem>}
              {(quote.status === 'accepted' || quote.status === 'declined') && (
                <DropdownMenuItem onSelect={acties.onTerug}><RotateCcw className="mr-2 h-4 w-4" />Besluit terugdraaien</DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={acties.onDupliceer}><Copy className="mr-2 h-4 w-4" />Dupliceren</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={acties.onVerwijder} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Verwijderen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono text-sm text-muted-foreground">{quote.quote_number}</span>
          <Titel titel={quote.title} onBewaar={acties.onTitel} />
          <StatusLabel weergave={w} />
          {sig && <SignaalLabel signaal={sig} />}
          <Bewaarstand stand={bewaarstand} />
        </div>
        <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
          {t ? (
            <Link to={t.href} className="font-medium text-foreground hover:underline">
              {t.soort === 'kavel' ? 'Kavel ' : t.soort === 'opportunity' ? 'Opportunity ' : ''}{t.label}
            </Link>
          ) : (
            <span>Geen project</span>
          )}
          {onderregel.map((d) => <span key={d}>· {d}</span>)}
        </p>
      </div>

      <dl className="m-0 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Feit label={quote.pricing_mode === 'exclusive' ? 'Bedrag excl. btw' : 'Bedrag incl. btw'}>{euro(werkBedrag)}</Feit>
        <Feit label="Geldig tot">
          <Bewerkbaar
            label="Geldig tot"
            type="date"
            waarde={quote.valid_until ?? ''}
            weergave={<span className={cn(sig?.soort === 'verlopen' && 'text-red-700')}>{datum(quote.valid_until)}</span>}
            onBewaar={(v) => acties.onDatum('valid_until', v || null)}
          />
        </Feit>
        <Feit label="Offertedatum">
          <Bewaarbaar waarde={quote.quote_date} onBewaar={(v) => acties.onDatum('quote_date', v)} />
        </Feit>
        <Feit label="Revisie">
          {huidig}
          <span className="text-xs font-normal text-muted-foreground">
            {' '}· {laatsteBestand ? `PDF v${laatsteBestand.version_number}.0 in de map` : metProject ? 'nog geen PDF in de map' : `${revisies.length} vastgelegd`}
          </span>
        </Feit>
        {quote.status !== 'draft' && quote.sent_on && (
          <Feit label="Gemarkeerd als verstuurd">
            {datum(quote.sent_on)}
            {quote.sent_to && <span className="text-xs font-normal text-muted-foreground"> · aan {quote.sent_to}</span>}
            {quote.sent_revision && <span className="text-xs font-normal text-muted-foreground"> · rev. {quote.sent_revision}</span>}
          </Feit>
        )}
        {(quote.status === 'accepted' || quote.status === 'declined') && (
          <Feit label={quote.status === 'accepted' ? 'Geaccepteerd' : 'Afgewezen'}>
            {quote.decided_on ? datum(quote.decided_on) : '—'}
            {quote.decision_note && <span className="text-xs font-normal text-muted-foreground"> · {quote.decision_note}</span>}
          </Feit>
        )}
      </dl>
    </section>
  );
}

function Bewaarbaar({ waarde, onBewaar }: { waarde: string | null; onBewaar: (v: string | null) => void }) {
  return (
    <Bewerkbaar label="Offertedatum" type="date" waarde={waarde ?? ''} weergave={datum(waarde)} onBewaar={(v) => onBewaar(v || null)} />
  );
}
